import { effect, EffectCleanupRegisterFn, EffectRef, Signal, untracked } from '@angular/core';

/**
 * Get the types an array of Signals contains:
 * [Signal<number>, Signal<string>] => [number, string].
 */
type Unwrapped<T> = {
  [K in keyof T]: T[K] extends Signal<infer U> ? U : never;
};

interface WatchOptions {
  defer?: boolean;
}

export function watch<Inputs extends readonly Signal<any>[]>(
  inputs: readonly [...Inputs],
  cb: (values: Unwrapped<Inputs>, onCleanup: EffectCleanupRegisterFn) => void,
  { defer = false }: WatchOptions = {}
) {
  let isFirstRun = true;

  return effect((onCleanup) => {
    // Read dependencies
    const value = inputs.map((input) => input()) as Unwrapped<Inputs>;

    if (defer === false || isFirstRun === false) {
      untracked(() => {
        cb(value, onCleanup);
      });
    }

    isFirstRun = false;
  });
}

export function watchOnce<Inputs extends readonly Signal<any>[]>(
  inputs: readonly [...Inputs],
  cb: (values: Unwrapped<Inputs>, onCleanup: EffectCleanupRegisterFn) => void,
  { defer = false }: WatchOptions = {}
) {
  const handle = watch(
    inputs,
    (values, onCleanup) => {
      cb(values, onCleanup);

      handle.destroy();
    },
    { defer }
  );

  return handle;
}

export interface PausableWatch {
  effectRef: EffectRef;

  /**
   * Ignore the next {@link amount} update calls.
   * @param amount Number of update calls to ignore.
   */
  ignoreNextUpdate(amount?: number): void;

  /**
   * Pause the watcher.
   */
  pause(): void;

  /**
   * Resume the watcher.
   */
  resume(): void;
}

export function pausableWatch<Inputs extends readonly Signal<any>[]>(
  inputs: readonly [...Inputs],
  cb: (values: Unwrapped<Inputs>, onCleanup: EffectCleanupRegisterFn) => void,
  { defer = false }: WatchOptions = {}
): PausableWatch {
  let ignore = 0;
  let paused = false;
  const effectRef = watch(
    inputs,
    (inputs, onCleanup) => {
      if (ignore > 0) {
        ignore--;
        return;
      }

      if (paused === false) {
        cb(inputs, onCleanup);
      }
    },
    { defer }
  );

  return {
    effectRef,
    ignoreNextUpdate: ignoreNext,
    pause,
    resume,
  };

  function ignoreNext(amount: number = 1): void {
    ignore = amount;
  }

  function pause(): void {
    paused = true;
  }

  function resume(): void {
    paused = false;
  }
}
